import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Building2,
  Ticket,
  Trophy,
  User,
  LogIn,
  Bell,
} from 'lucide-react';
import {
  DesktopNav,
  MobileNav,
  Button,
  Avatar,
  useViewport,
  type NavItem,
} from '@raffle/ui';
import { useAuth } from '../../providers/AuthProvider';

const navItems: NavItem[] = [
  { id: 'home', label: 'Home', href: '/', icon: <Home className="w-full h-full" /> },
  { id: 'properties', label: 'Properties', href: '/properties', icon: <Building2 className="w-full h-full" /> },
  { id: 'tickets', label: 'My Tickets', href: '/tickets', icon: <Ticket className="w-full h-full" /> },
  { id: 'draws', label: 'Draws', href: '/draws', icon: <Trophy className="w-full h-full" /> },
  { id: 'profile', label: 'Profile', href: '/profile', icon: <User className="w-full h-full" /> },
];

const Logo: React.FC = () => (
  <a href="/" className="flex items-center gap-2.5 group">
    {/* Logo mark — Nigerian green-white-green stripes with gold accent */}
    <div className="relative w-10 h-10 rounded-lg overflow-hidden shadow-lg shadow-gold-500/20 group-hover:shadow-gold-500/40 transition-shadow">
      {/* Nigerian flag vertical stripes */}
      <div className="absolute inset-0 flex">
        <div className="w-1/3 bg-[#008751]" />
        <div className="w-1/3 bg-white" />
        <div className="w-1/3 bg-[#008751]" />
      </div>
      {/* Overlay with brand text */}
      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
        <span className="text-gold-500 font-black text-sm tracking-tight">OYO</span>
      </div>
      {/* Bottom gold accent bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600" />
    </div>
    {/* Brand text — always visible, not hidden on mobile */}
    <div className="flex flex-col">
      <span className="font-display text-base sm:text-lg font-bold text-text-primary leading-none">
        Own Your <span className="text-gold-500">Own</span>
      </span>
      <span className="text-[9px] sm:text-[10px] text-text-muted leading-tight mt-0.5">Be your Oga's neighbour 🇳🇬</span>
    </div>
  </a>
);

export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useViewport();
  const { user, isAuthenticated } = useAuth();

  const getActiveItem = (): string => {
    const path = location.pathname;
    if (path === '/') return 'home';
    const item = navItems.find((item) => path.startsWith(item.href) && item.href !== '/');
    return item?.id || 'home';
  };

  const handleNavClick = (item: NavItem) => {
    navigate(item.href);
  };

  const RightContent = () => (
    <div className="flex items-center gap-3">
      {isAuthenticated ? (
        <>
          <button className="relative p-2 rounded-full hover:bg-white/5 transition-colors">
            <Bell className="w-5 h-5 text-text-secondary" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-vault-error rounded-full" />
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2"
          >
            <Avatar
              src={user?.avatarUrl}
              alt={`${user?.firstName} ${user?.lastName}`}
              fallback={`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
              size="sm"
            />
            {isDesktop && (
              <span className="text-sm font-medium text-text-primary">
                {user?.firstName}
              </span>
            )}
          </button>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/login')}
          >
            <LogIn className="w-4 h-4 mr-2" />
            Sign In
          </Button>
          <Button
            variant="gold"
            size="sm"
            onClick={() => navigate('/register')}
          >
            Get Started
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="page-container">
      {/* Desktop Navigation */}
      {isDesktop && (
        <DesktopNav
          logo={<Logo />}
          items={navItems}
          activeItem={getActiveItem()}
          onItemClick={handleNavClick}
          rightContent={<RightContent />}
        />
      )}

      {/* Mobile Top Bar (auth actions) */}
      {!isDesktop && (
        <header className="sticky top-0 z-40 bg-surface-0/95 backdrop-blur-sm border-b border-white/5">
          <div className="content-wrapper h-16 flex items-center justify-between">
            <Logo />
            <RightContent />
          </div>
        </header>
      )}

      {/* Page Content */}
      <main className={isDesktop ? 'pt-nav-desktop' : 'pb-nav-mobile'}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Mobile Navigation */}
      {!isDesktop && (
        <MobileNav
          items={navItems}
          activeItem={getActiveItem()}
          onItemClick={handleNavClick}
        />
      )}
    </div>
  );
};
